import type { FullRecord } from "@shared/types";
import { SL, type Visao } from "../constants";
import { padZ, parseDtData } from "../util";

export interface FilterState {
  visao: Visao;
  search: string;
  ramo: string;
  status: string;
  resp: string;
  dtDe: string;
  dtAte: string;
}

export function initialFilterState(): FilterState {
  return { visao: "tratativa", search: "", ramo: "", status: "", resp: "", dtDe: "", dtAte: "" };
}

export function applyFilter(records: FullRecord[], f: FilterState): FullRecord[] {
  const q = f.search.toLowerCase();
  const dtDe = f.dtDe ? new Date(f.dtDe + "T00:00:00") : null;
  const dtAte = f.dtAte ? new Date(f.dtAte + "T23:59:59") : null;
  const statusVis = f.visao === "tratativa" ? ["novo", "cotacao", "analise"]
    : f.visao === "efetivado" ? ["efetivado"]
    : f.visao === "perdido" ? ["perdido"] : null;

  return records.filter((d) => {
    if (statusVis && !statusVis.includes(d.status)) return false;
    if (q && !`${d.cliente} ${d.ramo || ""} ${d.corretor || ""} ${d.obs || ""} ${d.resp || ""} ${d.prox_mot || ""} ${d.motivo_perda || ""} ${d.motivo_det || ""}`.toLowerCase().includes(q)) return false;
    if (f.ramo && d.ramo !== f.ramo) return false;
    if (f.status && d.status !== f.status) return false;
    if (f.resp && d.resp !== f.resp) return false;
    if (dtDe || dtAte) {
      const dtReg = parseDtData(d.data);
      if (!dtReg) return false;
      if (dtDe && dtReg < dtDe) return false;
      if (dtAte && dtReg > dtAte) return false;
    }
    return true;
  });
}

interface Props {
  records: FullRecord[];
  filter: FilterState;
  onChange: (f: FilterState) => void;
}

export function FilterToolbar({ records, filter, onChange }: Props) {
  const ramos = [...new Set(records.map(d => d.ramo).filter((r): r is string => !!r))].sort();
  const resps = [...new Set(records.map(d => d.resp).filter((r): r is string => !!r))].sort();

  function setVisao(v: Visao) {
    onChange({ ...filter, visao: v, status: "" });
  }

  function setMes(offset: number) {
    const n = new Date();
    const m = n.getMonth() + offset;
    const primeiro = new Date(n.getFullYear(), m, 1);
    const ultimo = new Date(n.getFullYear(), m + 1, 0);
    onChange({
      ...filter,
      dtDe: `${primeiro.getFullYear()}-${padZ(primeiro.getMonth() + 1)}-01`,
      dtAte: `${ultimo.getFullYear()}-${padZ(ultimo.getMonth() + 1)}-${padZ(ultimo.getDate())}`,
    });
  }

  function setTrimestre() {
    const n = new Date();
    const trim = Math.floor(n.getMonth() / 3);
    const primeiro = new Date(n.getFullYear(), trim * 3, 1);
    const ultimo = new Date(n.getFullYear(), trim * 3 + 3, 0);
    onChange({
      ...filter,
      dtDe: `${primeiro.getFullYear()}-${padZ(primeiro.getMonth() + 1)}-01`,
      dtAte: `${ultimo.getFullYear()}-${padZ(ultimo.getMonth() + 1)}-${padZ(ultimo.getDate())}`,
    });
  }

  function setAno() {
    const y = new Date().getFullYear();
    onChange({ ...filter, dtDe: `${y}-01-01`, dtAte: `${y}-12-31` });
  }

  function limparDatas() {
    onChange({ ...filter, dtDe: "", dtAte: "" });
  }

  const pillStyle = (active: boolean) => ({
    padding: "7px 16px", fontSize: 13, fontWeight: 500 as const, borderRadius: 20, cursor: "pointer", fontFamily: "inherit",
    border: ".5px solid " + (active ? "var(--tx)" : "var(--bd2)"),
    background: active ? "var(--tx)" : "var(--sf)",
    color: active ? "#fff" : "var(--tx2)",
  });

  return (
    <>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <button style={pillStyle(filter.visao === "tratativa")} onClick={() => setVisao("tratativa")}>Em tratativa</button>
        <button style={pillStyle(filter.visao === "todos")} onClick={() => setVisao("todos")}>Todos</button>
        <button style={pillStyle(filter.visao === "efetivado")} onClick={() => setVisao("efetivado")}>Efetivados</button>
        <button style={pillStyle(filter.visao === "perdido")} onClick={() => setVisao("perdido")}>Não efetivados</button>
      </div>

      <div className="toolbar">
        <input type="text" placeholder="Buscar cliente, ramo, corretor, motivo..." value={filter.search} onChange={e => onChange({ ...filter, search: e.target.value })} />
        <select value={filter.ramo} onChange={e => onChange({ ...filter, ramo: e.target.value })}>
          <option value="">Todos os ramos</option>
          {ramos.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filter.status} onChange={e => onChange({ ...filter, status: e.target.value })}>
          <option value="">Todos os status</option>
          {Object.entries(SL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filter.resp} onChange={e => onChange({ ...filter, resp: e.target.value })}>
          <option value="">Todos os responsáveis</option>
          {resps.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--tx2)", fontWeight: 500, whiteSpace: "nowrap" }}>Filtrar por data:</span>
        <input type="date" value={filter.dtDe} onChange={e => onChange({ ...filter, dtDe: e.target.value })} title="Data inicial" style={{ padding: "7px 10px", fontSize: 13, border: ".5px solid var(--bd2)", borderRadius: "var(--r)", background: "var(--sf)", color: "var(--tx)", fontFamily: "inherit", outline: "none" }} />
        <span style={{ fontSize: 12, color: "var(--tx3)" }}>até</span>
        <input type="date" value={filter.dtAte} onChange={e => onChange({ ...filter, dtAte: e.target.value })} title="Data final" style={{ padding: "7px 10px", fontSize: 13, border: ".5px solid var(--bd2)", borderRadius: "var(--r)", background: "var(--sf)", color: "var(--tx)", fontFamily: "inherit", outline: "none" }} />
        <button onClick={() => setMes(0)} style={{ padding: "6px 11px", fontSize: 12, fontWeight: 500, border: ".5px solid var(--bd2)", borderRadius: 8, background: "var(--sf)", color: "var(--tx)", cursor: "pointer", fontFamily: "inherit" }}>Este mês</button>
        <button onClick={() => setMes(-1)} style={{ padding: "6px 11px", fontSize: 12, fontWeight: 500, border: ".5px solid var(--bd2)", borderRadius: 8, background: "var(--sf)", color: "var(--tx)", cursor: "pointer", fontFamily: "inherit" }}>Mês anterior</button>
        <button onClick={setTrimestre} style={{ padding: "6px 11px", fontSize: 12, fontWeight: 500, border: ".5px solid var(--bd2)", borderRadius: 8, background: "var(--sf)", color: "var(--tx)", cursor: "pointer", fontFamily: "inherit" }}>Trimestre atual</button>
        <button onClick={setAno} style={{ padding: "6px 11px", fontSize: 12, fontWeight: 500, border: ".5px solid var(--bd2)", borderRadius: 8, background: "var(--sf)", color: "var(--tx)", cursor: "pointer", fontFamily: "inherit" }}>Este ano</button>
        <button onClick={limparDatas} style={{ padding: "6px 11px", fontSize: 12, fontWeight: 500, border: ".5px solid var(--bd2)", borderRadius: 8, background: "var(--sf)", color: "var(--tx3)", cursor: "pointer", fontFamily: "inherit" }}>Limpar</button>
      </div>
    </>
  );
}
